<template>
  <div class="barcode-scanner-wrapper p-3">
    <!-- USB ready indicator -->
    <div class="usb-indicator" :class="{ 'usb-flash': flashActive }">
      <span class="usb-dot"></span>
      ماسح USB جاهز
      <span v-if="lastScanned" class="ms-2 text-success fw-bold">{{ lastScanned }}</span>
    </div>

    <!-- Camera toggle -->
    <button
      class="btn btn-sm mt-2"
      :class="cameraScanning ? 'btn-danger' : 'btn-outline-secondary'"
      @click="toggleCamera"
    >
      <i class="pi" :class="cameraScanning ? 'pi-times' : 'pi-camera'"></i>
      {{ cameraScanning ? 'إغلاق الكاميرا' : 'تشغيل الكاميرا' }}
    </button>

    <div v-if="cameraScanning" class="camera-viewport mt-2">
      <div :id="scannerDivId"></div>
    </div>

    <p v-if="errorMsg" class="text-danger mt-1 mb-0 small">{{ errorMsg }}</p>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';

export default {
  name: 'BarcodeScanner',

  props: {
    mode: { type: String, required: true },
    // The consuming page keeps this true through the payment action. This
    // extends the scanner's own resolve lock across the complete operation.
    processing: { type: Boolean, default: false },
  },

  emits: ['scanned', 'scan-error'],

  data() {
    return {
      cameraScanning: false,
      scanner: null,
      scannerDivId: `bc-reader-${Math.random().toString(36).substr(2, 9)}`,
      errorMsg: '',
      flashActive: false,
      lastScanned: '',

      keyBuffer:   [],
      lastKeyTime: 0,
      bufferTimer: null,

      isProcessingScan: false,
      recentScanTimes: {},
      lastAcceptedScanValue: null,
      DEBOUNCE_MS: 2000,

      // A USB HID keyboard-wedge scanner sends its whole payload in a few
      // milliseconds per character — far faster than any human can type.
      // MIN_SCAN_LENGTH filters out stray 1-2 key noise; SCAN_BURST_GAP_MS is
      // the max gap allowed between two keystrokes for them to still count as
      // part of the same scanner burst (well below realistic human typing
      // speed, so normal typing in a focused field never accumulates a run
      // long/fast enough to be mistaken for a scan).
      MIN_SCAN_LENGTH: 3,
      SCAN_BURST_GAP_MS: 40,
    };
  },

  methods: {
    // ── USB keyboard scanner ────────────────────────────────────────────────

    // IMPORTANT: a keyboard-wedge scanner injects keystrokes into whichever
    // element already has focus — it does not change document.activeElement.
    // This used to `return` early whenever an <input>/<textarea>/<select> was
    // focused, which silently swallowed every scan whenever the page's search
    // box (the most commonly-focused element on both Attendance and Payments)
    // had focus. That was the actual bug behind "scanner reads the code but
    // nothing happens". Detection now relies purely on TIMING (see
    // SCAN_BURST_GAP_MS/MIN_SCAN_LENGTH above), not on what currently has
    // focus, so it works regardless of where the cursor happens to be.
    handleKeydown(e) {
      const now = Date.now();

      if (e.key === 'Enter') {
        clearTimeout(this.bufferTimer);
        const buffered = this.keyBuffer.splice(0);
        if (buffered.length >= this.MIN_SCAN_LENGTH) this.processScanned(buffered.join(''));
        return;
      }

      // Reset buffer if the gap since the last key is too large to be a
      // scanner burst (i.e. this looks like normal human typing).
      if (now - this.lastKeyTime > this.SCAN_BURST_GAP_MS && this.keyBuffer.length) {
        this.keyBuffer = [];
        clearTimeout(this.bufferTimer);
      }

      if (e.key.length === 1) {
        this.keyBuffer.push(e.key);
        this.lastKeyTime = now;

        clearTimeout(this.bufferTimer);
        this.bufferTimer = setTimeout(() => {
          const buffered = this.keyBuffer.splice(0);
          if (buffered.length >= this.MIN_SCAN_LENGTH) this.processScanned(buffered.join(''));
        }, 200);
      }
    },

    // ── Parse & emit ────────────────────────────────────────────────────────

    // Printed barcodes (BarcodeLabel.vue, legacy) encode "STU-<id>"; older
    // printed QR codes (students_qr.vue / students_qr_print.vue) encode the
    // bare numeric id. Current Student Barcodes QR codes encode a full URL —
    // https://mobile.tarekmath.com/qr/{token} — the opaque token is resolved
    // to a student server-side (never exposes the id/phone client-side).
    // Anything else (reservation code, reservation id, ...) is passed through
    // as `raw` for the consumer to interpret.
    async processScanned(raw) {
      console.log('[BarcodeScanner] QR/barcode detected');

      const value = String(raw ?? '').trim();
      if (!value) return;

      console.log('[BarcodeScanner] Decoded value:', value);

      if (this.isProcessingScan || this.processing) {
        console.log('[BarcodeScanner] Scan ignored while another scan/action is processing');
        return;
      }

      const now = Date.now();
      const lastSeenAt = this.recentScanTimes[value] ?? 0;
      if (now - lastSeenAt < this.DEBOUNCE_MS) {
        console.log('[BarcodeScanner] Duplicate scan ignored (within debounce window):', value);
        return;
      }
      for (const [scanValue, scannedAt] of Object.entries(this.recentScanTimes)) {
        if (now - scannedAt >= this.DEBOUNCE_MS) delete this.recentScanTimes[scanValue];
      }
      this.recentScanTimes[value] = now;
      this.lastAcceptedScanValue = value;
      this.isProcessingScan = true;
      this.errorMsg = '';

      try {
        const stuMatch = value.match(/^STU-(\d+)$/i);
        const qrUrlMatch = value.match(/\/qr\/([A-Za-z0-9]+)\/?(?:[?#].*)?$/);

        if (stuMatch || (!qrUrlMatch && /^\d+$/.test(value))) {
          const studentId = stuMatch ? stuMatch[1] : value;
          this.flashScan(studentId ? `STU-${studentId}` : value);
          this.$emit('scanned', { raw: value, studentId, mode: this.mode });
          return;
        }

        if (qrUrlMatch) {
          this.flashScan(value);
          await this.resolveQrToken(qrUrlMatch[1], value);
          return;
        }

        // Reservation code / free-text value — no local id to extract; the
        // consumer falls back to searching by the raw scanned value.
        this.flashScan(value);
        this.$emit('scanned', { raw: value, studentId: null, mode: this.mode });
      } finally {
        this.isProcessingScan = false;
      }
    },

    flashScan(label) {
      this.lastScanned  = label;
      this.flashActive  = true;
      setTimeout(() => { this.flashActive = false; }, 600);
    },

    // Resolve the opaque token once, preserving studentId for existing consumers
    // while also passing the full authoritative identity to optimized flows.
    async resolveQrToken(token, raw) {
      try {
        const { data } = await axios.get(`/qr/resolve/${token}`);
        const resolvedStudent = data?.data ?? null;
        const studentId = resolvedStudent?.user_id != null ? String(resolvedStudent.user_id) : null;

        if (!studentId) throw new Error('QR resolve response did not include user_id');

        this.$emit('scanned', {
          raw,
          qrToken: token,
          studentId,
          resolvedStudent,
          mode: this.mode,
        });
      } catch (err) {
        console.log('[BarcodeScanner] QR token resolve failed:', err);
        const message = err.response?.data?.message ?? 'QR غير صالح';

        // Hardware scanner pages use their brief notification stacks and also
        // need the raw value to clear any focused manual-search input safely.
        if (this.mode === 'payment' || this.mode === 'attendance') {
          this.$emit('scan-error', { message, raw, mode: this.mode });
        } else {
          this.errorMsg = message;
        }
      }
    },

    // ── Camera ──────────────────────────────────────────────────────────────

    async toggleCamera() {
      this.cameraScanning ? await this.stopCamera() : await this.startCamera();
    },

    async startCamera() {
      this.errorMsg      = '';
      this.cameraScanning = true;
      await this.$nextTick();

      try {
        this.scanner = new Html5Qrcode(this.scannerDivId);
        await this.scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 350, height: 100 } },
          (decoded) => this.processScanned(decoded),
          () => {}
        );
        console.log('[BarcodeScanner] Scanner initialized (camera)');
      } catch {
        this.errorMsg      = 'تعذّر الوصول إلى الكاميرا. يرجى السماح بالإذن وإعادة المحاولة.';
        this.cameraScanning = false;
      }
    },

    async stopCamera() {
      if (this.scanner?.isScanning) {
        try { await this.scanner.stop(); } catch {}
      }
      this.scanner       = null;
      this.cameraScanning = false;
    },
  },

  watch: {
    // Restart the cooldown when the consuming action finishes, so the camera's
    // next frame cannot immediately resolve the same token again.
    processing(value, previous) {
      if (!value && previous && this.lastAcceptedScanValue) {
        // Attendance relies on the processing lock while its request is active,
        // then becomes ready immediately. Payment keeps its short post-action
        // cooldown to suppress repeated camera frames for the same receipt.
        if (this.mode === 'attendance') delete this.recentScanTimes[this.lastAcceptedScanValue];
        else this.recentScanTimes[this.lastAcceptedScanValue] = Date.now();
      }
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
    console.log('[BarcodeScanner] Scanner initialized (USB keyboard-wedge)', { mode: this.mode });
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    clearTimeout(this.bufferTimer);
    this.stopCamera();
  },
};
</script>

<style scoped>
.barcode-scanner-wrapper {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
}

.usb-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #15803d;
  transition: background 0.3s;
}

.usb-indicator.usb-flash {
  background: #dcfce7;
  border-color: #22c55e;
}

.usb-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: pulse-dot 1.5s infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.camera-viewport {
  border: 2px solid #0d6efd;
  border-radius: 8px;
  overflow: hidden;
  max-width: 400px;
}
</style>
